import type { SitemapType } from 'expo-router';

import { getLegacyRedirectPathname } from 'navigation/routing/legacy-redirects';
import type { IStaticPageRoute } from 'remote/queries/static-page';

/** Search params are already decoded by Expo Router. Keep query/hash encoding intact. */
export function getDestinationPathname(destination: unknown): string | null {
  if (typeof destination !== 'string' || !destination.startsWith('/')) {
    return null;
  }
  try {
    // Validate escapes in the entire value, but only decode the pathname for matching.
    decodeURIComponent(destination);
    const encodedPathname = destination.split(/[?#]/)[0];
    const pathname = decodeURIComponent(encodedPathname);
    const hasControlCharacters = (value: string) =>
      Array.from(value).some(
        character => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127
      );
    if (
      /[\\\s]/.test(destination) ||
      /[\\\s%?#()[\]]/.test(pathname) ||
      hasControlCharacters(destination) ||
      hasControlCharacters(pathname) ||
      /%2f/i.test(encodedPathname) ||
      pathname.includes('//') ||
      pathname.split('/').some(segment => segment === '.' || segment === '..') ||
      /^\/autoselect(?:\/|$)/i.test(pathname)
    ) {
      return null;
    }
    return pathname.replace(/\/$/, '') || '/';
  } catch {
    return null;
  }
}

/** Match filesystem routes from Expo, including dynamic segments, without a second route list. */
export function matchesAppRoute(pathname: string, sitemap: SitemapType | null): boolean {
  if (!sitemap) {
    return false;
  }
  const resolvedPathname = getLegacyRedirectPathname(pathname) ?? pathname;
  const href = typeof sitemap.href === 'string' ? sitemap.href : sitemap.href.pathname;
  const segments = (href ?? '').split('/').filter(segment => segment && !/^\(.*\)$/.test(segment));
  const pathSegments = resolvedPathname.split('/').filter(Boolean);
  const isLayout = /(?:^|\/)_layout(?:\.[^.]+)?\.[jt]sx?$/.test(sitemap.contextKey);
  const matches =
    !isLayout &&
    !sitemap.isGenerated &&
    !sitemap.isInternal &&
    segments.length === pathSegments.length &&
    segments.every((segment, index) =>
      /^\[[^\]]+\]$/.test(segment) ? !!pathSegments[index] : segment === pathSegments[index]
    );
  return matches || sitemap.children.some(child => matchesAppRoute(pathname, child));
}

export function matchesStaticPage(pathname: string, staticRoutes: IStaticPageRoute[]): boolean {
  const slug = (getLegacyRedirectPathname(pathname) ?? pathname).replace(/^\//, '').toLowerCase();
  return staticRoutes.some(route =>
    [route.path, ...Object.values(route.localePath ?? {})].some(path => path?.current === slug)
  );
}
